/**
 * Document-defaults model — the wire shape returned by
 * `GET /api/mobile/v1/account/document-defaults` and patched by
 * `PATCH /api/mobile/v1/account/document-defaults`.
 *
 * These are the company-wide defaults that get baked into every RAMS the
 * user generates: ten free-text statement fields (training, fire safety,
 * asbestos, waste, etc.) plus a list of legislation references. The web
 * route at `app/api/account/document-defaults/route.ts` is the canonical
 * server-side behaviour; the mobile route at
 * `app/api/mobile/v1/account/document-defaults/route.ts` proxies the same
 * Prisma model (`companyDocumentDefaults`) to keep the surfaces in sync.
 *
 * Statements are typed as `z.string().nullable()` rather than `z.string()`
 * even though the Prisma model has them as non-null — `nullable()` is
 * defensively tolerant for read-time drift (older rows / partial DB seeds)
 * while writes via `DocumentDefaultsUpdate` can omit fields entirely.
 */
import { z } from "zod";

export const DocumentDefaultsSchema = z.object({
  trainingStatement: z.string().nullable(),
  fireSafetyStatement: z.string().nullable(),
  asbestosStatement: z.string().nullable(),
  wasteStatement: z.string().nullable(),
  hazardousWasteStatement: z.string().nullable(),
  emergencyStatement: z.string().nullable(),
  firstAidStatement: z.string().nullable(),
  welfareStatement: z.string().nullable(),
  manualHandlingStatement: z.string().nullable(),
  ppeStatement: z.string().nullable(),
  legislationRefs: z.array(z.string()),
});
export type DocumentDefaults = z.infer<typeof DocumentDefaultsSchema>;

export const DocumentDefaultsUpdateSchema = DocumentDefaultsSchema.partial();
export type DocumentDefaultsUpdate = z.infer<typeof DocumentDefaultsUpdateSchema>;

export const DocumentDefaultsResponseSchema = z.object({
  defaults: DocumentDefaultsSchema,
});
export type DocumentDefaultsResponse = z.infer<
  typeof DocumentDefaultsResponseSchema
>;

/** Metadata returned only by the additive company defaults API. */
export const CompanyDocumentDefaultsResponseSchema = z.object({
  defaults: DocumentDefaultsSchema,
  /** Suggested values are not persisted until a manager saves them. */
  source: z.enum(["company", "suggested"]),
  hasUnassignedLegacyDefaults: z.boolean(),
  canManage: z.boolean(),
});
export type CompanyDocumentDefaultsResponse = z.infer<
  typeof CompanyDocumentDefaultsResponseSchema
>;
